import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentUserIsGuest, getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getLocation, getReturnUrl } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import signInAsGuest from "@insite/client-framework/Store/Pages/SignIn/Handlers/SignInAsGuest";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps, GridOffset } from "@insite/mobius/GridContainer"; // Store Custom Code
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Typography, { TypographyProps } from "@insite/mobius/Typography/Typography";
import breakpointMediaQueries, { mediaOptions } from "@insite/mobius/utilities/breakpointMediaQueries"; // Store Custom Code
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import React, { FC } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

/* Store Custom Code - Start */
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Icon, { IconProps } from "@insite/mobius/Icon";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import SignInUser from "@store/Library/Components/Icons/SignInUser";
import CreateAccountModal from "@store/Library/Components/Store/StoreCreateAccountModal";
import breakpointValuesMediaQueries from "@store/Framework/Common/Utilities/breakpointValuesMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
/* Store Custom Code - End */

const enum fields {
    text = "text",
    fullWidthButton = "fullWidthButton",
    // Store Custom Code
    newAccountText = "newAccountText",
    guestText = "guestText",
}

const mapStateToProps = (state: ApplicationState) => {
    let { search } = getLocation(state);
    const { allowCreateAccount, allowGuestCheckout } = getSettingsCollection(state).accountSettings;
    let returnUrl = getReturnUrl(state);
    if (!returnUrl && state.components.signInOverlay.isOpen) {
        returnUrl = getLocation(state).pathname;
        if (!search) {
            search = `?returnUrl=${returnUrl}`;
        }
    }

    const shippingPageUrl = getPageLinkByPageType(state, "CheckoutShippingPage")?.url;
    const referredFromShipping = returnUrl === shippingPageUrl;
    const onePageCheckoutPageUrl = getPageLinkByPageType(state, "OnePageCheckoutPage")?.url;
    const referredFromOnePageCheckout = returnUrl === onePageCheckoutPageUrl;
    const createAccountPageLink = getPageLinkByPageType(state, "CreateAccountPage");

    return {
        allowCreateAccount,
        allowGuestCheckout: allowGuestCheckout && !getCurrentUserIsGuest(state), // Store Custom Code
        returnUrl,
        createAccountUrl: createAccountPageLink ? `${createAccountPageLink.url}${search}` : undefined,
    };
};

const mapDispatchToProps = {
    signInAsGuest,
};

interface OwnProps extends WidgetProps {
    fields: {
        [fields.text]: string;
        [fields.fullWidthButton]: boolean;
        // Store Custom Code
        [fields.newAccountText]: string;
        [fields.guestText]: string;
    };
}

type Props = ReturnType<typeof mapStateToProps> & OwnProps & ResolveThunks<typeof mapDispatchToProps> & HasHistory;

export interface SignInCreateNewAccountStyles {
    signInCreateNewAccountGridContainer?: GridContainerProps;
    signInCreateNewAccountTitleGridItem?: GridItemProps;
    signInCreateNewAccountTextGridItem?: InjectableCss; // Store Custom Code
    signInCreateNewAccountButtonGridItem?: InjectableCss; // Store Custom Code
    signInCreateNewAccountTitle?: TypographyProps;
    signInCreateNewAccountText?: TypographyProps;
    checkoutAsGuestButton?: ButtonPresentationProps;
    createNewAccountButton?: ButtonPresentationProps;
    // Store Custom Code
    containerGridItem?: GridItemProps;
    accountIcon?: IconProps;
}

export const signInCreateNewAccountStyles: SignInCreateNewAccountStyles = {
    /* Store Custom Code - Start */
    signInCreateNewAccountGridContainer: {
        gap: 48,
        css: css`
            ${GridOffset} {
                ${({ theme }) =>
                    breakpointValuesMediaQueries(
                        theme,
                        [{ values: ["center", null, "flex-start", null, null], properties: ["justify-content"] }],
                        mediaOptions.min,
                    )}
            }
        `,
    },
    signInCreateNewAccountTitle: {
        variant: "h2",
        css: css`
            margin-top: 15px;
        `,
    },
    signInCreateNewAccountTitleGridItem: {
        width: 12,
    },
    signInCreateNewAccountTextGridItem: {
        css: css`
            margin-top: 20px;
        `,
    },
    signInCreateNewAccountButtonGridItem: {
        css: css`
            margin-top: 40px;

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            flex-direction: column;
                        `,
                        null,
                        css`
                            flex-direction: row;
                        `,
                        css`
                            flex-direction: column;
                        `,
                        css`
                            flex-direction: row;
                            justify-content: space-between;
                        `,
                    ],
                    "min",
                )}
        `,
    },
    checkoutAsGuestButton: { variant: "secondary" },
    /* Store Custom Code - End */
    createNewAccountButton: { variant: "secondary" },
    // Store Custom Code
    containerGridItem: {
        width: [10, 8, 12, 10, 10],
        css: css`
            display: flex;
            flex-direction: column;
        `,
    },
    accountIcon: {
        src: SignInUser,
        size: 60,
        css: css`
            color: ${getColor("secondary.main")};
        `,
    },
};

/**
 * @deprecated Use signInCreateNewAccountStyles instead.
 */
export const signInCreateNewAccount = signInCreateNewAccountStyles;
const styles = signInCreateNewAccountStyles;

const SignInCreateNewAccount: FC<Props> = ({
    history,
    fields,
    allowCreateAccount,
    allowGuestCheckout,
    returnUrl,
    createAccountUrl,
    signInAsGuest,
}) => {
    if (!allowCreateAccount) {
        return null;
    }

    const handleCheckoutAsGuest = () => {
        // Store Custom Code - Removed returnUrl condition

        signInAsGuest({ returnUrl });
    };

    const handleCreateNewAccount = () => {
        if (!createAccountUrl) {
            return;
        }

        history.push(createAccountUrl);
    };

    const createButtonCss = fields.fullWidthButton
        ? css`
              width: 100%;
          `
        : undefined;

    const { guestText, newAccountText } = fields; // Store Custom Code

    return (
        <GridContainer {...styles.signInCreateNewAccountGridContainer}>
            {/* Store Custom Code - Start */}
            <GridItem {...styles.containerGridItem}>
                <StyledWrapper>
                    <Icon {...styles.accountIcon} />
                    <Typography {...styles.signInCreateNewAccountTitle}>{translate("Create New Account")}</Typography>
                </StyledWrapper>
                {newAccountText && (
                    <StyledWrapper {...styles.signInCreateNewAccountTextGridItem}>
                        <Typography {...styles.signInCreateNewAccountText}>{newAccountText}</Typography>
                    </StyledWrapper>
                )}
                <StyledWrapper {...styles.signInCreateNewAccountButtonGridItem}>
                    {createAccountUrl && <CreateAccountModal b2cAccountUrl={createAccountUrl} />}
                </StyledWrapper>
            </GridItem>
            <GridItem {...styles.containerGridItem}>
                <StyledWrapper>
                    <Icon {...styles.accountIcon} />
                    <Typography {...styles.signInCreateNewAccountTitle}>
                        {translate("Prefer to shop and order as a guest?")}
                    </Typography>
                </StyledWrapper>
                {guestText && (
                    <StyledWrapper {...styles.signInCreateNewAccountTextGridItem}>
                        <Typography {...styles.signInCreateNewAccountText}>{guestText}</Typography>
                    </StyledWrapper>
                )}
                <StyledWrapper {...styles.signInCreateNewAccountButtonGridItem}>
                    {allowGuestCheckout && (
                        <Button
                            {...styles.checkoutAsGuestButton}
                            onClick={handleCheckoutAsGuest}
                            data-test-selector="signInCreateNewAccount_checkoutAsGuest"
                        >
                            {translate("Continue as Guest")}
                        </Button>
                    )}
                </StyledWrapper>
            </GridItem>
            {/* Store Custom Code - End */}
        </GridContainer>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withHistory(SignInCreateNewAccount)),
    definition: {
        allowedContexts: ["SignInPage", "SignInOverlay"],
        group: "Sign In",
        icon: "User",
        fieldDefinitions: [
            /* Store Custom Code - Start */
            {
                name: fields.newAccountText,
                displayName: "New Account Text",
                editorTemplate: "MultilineTextField",
                defaultValue: "Create an account to checkout faster, view order history, save product lists, and more!",
                fieldType: "Translatable",
            },
            {
                name: fields.guestText,
                displayName: "Guest Text",
                editorTemplate: "MultilineTextField",
                defaultValue: "Your personal information will only be needed to complete the transaction.",
                fieldType: "Translatable",
            },
            /* Store Custom Code - End */
            // Store Custom Code - Removed - fullWidthButton
        ],
    },
};

export default widgetModule;
